import type { Line } from "@/lib/types"

export interface LineUpsertBatches {
  withAssigneeUserId: Record<string, unknown>[]
  withoutAssigneeUserId: Record<string, unknown>[]
}

// lines テーブルへの upsert ペイロードを構築する。
// assignee_user_id は「ローカルに値があるときだけ」列を含める。
// 含めない場合、DBトリガーが設定した値（作成者デフォルト）が upsert の
// UPDATE 経路で NULL 上書きされずに保持される。
// PostgREST のバルク upsert は行間でキー構成が揃っている必要があるため、
// 列の有無で2バッチに分ける。
// Line のフィールドごとの「カラム名 + 値の正規化」。
// buildLineUpsertBatches の全体ペイロードと同じ正規化規則を共有する。
const LINE_COLUMN_BUILDERS: Partial<Record<keyof Line, (line: Line) => [string, unknown]>> = {
  type: (l) => ["type", l.type],
  content: (l) => ["content", l.content],
  status: (l) => ["status", l.status || null],
  duration: (l) => ["duration", l.duration || null],
  parentTaskId: (l) => ["parent_task_id", l.parentTaskId || null],
  bulletPoint: (l) => ["bullet_point", l.bulletPoint || null],
  indentLevel: (l) => ["indent_level", l.indentLevel || 0],
  listNumber: (l) => ["list_number", l.listNumber || null],
  scheduledDate: (l) => ["scheduled_date", l.scheduledDate || null],
  startedAt: (l) => ["started_at", l.startedAt || null],
  notes: (l) => ["notes", l.notes || null],
  priority: (l) => ["priority", l.priority || null],
  tags: (l) => ["tags", l.tags || []],
  assignee: (l) => ["assignee", l.assignee || null],
  repeat: (l) => ["repeat", l.repeat ?? null],
  reminder: (l) => ["reminder", l.reminder ?? null],
  sortOrder: (l) => ["sort_order", l.sortOrder ?? null],
  goalId: (l) => ["goal_id", l.goalId ?? null],
}

// 変更されたフィールドのカラムだけを含む部分更新ペイロードを構築する。
// 行全体の upsert は「ユーザーが触っていないフィールド」まで巻き添えで
// 上書きするため（外部/MCP の更新が消える）、update はこの部分ペイロードで送る。
// - orderIndex が指定されたら line_order を含める（並び替えマーカー "id" もこれで反映）
// - assignee_user_id は値があるときだけ含める（DBトリガーのデフォルト保持。全体 upsert と同じ規則）
export function buildPartialLineUpdatePayload(
  line: Line,
  changedFields: (keyof Line)[],
  orderIndex: number | null,
): Record<string, unknown> {
  const payload: Record<string, unknown> = {}

  for (const field of changedFields) {
    if (field === "assigneeUserId") {
      if (line.assigneeUserId !== undefined) {
        payload.assignee_user_id = line.assigneeUserId
      }
      continue
    }
    const builder = LINE_COLUMN_BUILDERS[field]
    if (!builder) continue // "id"(並び替えマーカー)等、カラムに対応しないフィールドは無視
    const [column, value] = builder(line)
    payload[column] = value
  }

  if (orderIndex !== null) {
    payload.line_order = orderIndex
  }

  return payload
}

export function buildLineUpsertBatches(
  lines: Line[],
  notebookId: string,
  orderIndexById: Map<string, number> | null
): LineUpsertBatches {
  const withAssigneeUserId: Record<string, unknown>[] = []
  const withoutAssigneeUserId: Record<string, unknown>[] = []

  lines.forEach((line, index) => {
    const row: Record<string, unknown> = {
      id: line.id,
      notebook_id: notebookId,
      type: line.type,
      content: line.content,
      status: line.status || null,
      duration: line.duration || null,
      parent_task_id: line.parentTaskId || null,
      created_at: line.createdAt,
      line_order: orderIndexById ? (orderIndexById.get(line.id) ?? -1) : index,
      bullet_point: line.bulletPoint || null,
      indent_level: line.indentLevel || 0,
      list_number: line.listNumber || null,
      scheduled_date: line.scheduledDate || null,
      started_at: line.startedAt || null,
      notes: line.notes || null,
      priority: line.priority || null,
      tags: line.tags || [],
      assignee: line.assignee || null,
      repeat: line.repeat ?? null,
      reminder: line.reminder ?? null,
      sort_order: line.sortOrder ?? null,
      goal_id: line.goalId ?? null,
    }

    if (line.assigneeUserId !== undefined) {
      row.assignee_user_id = line.assigneeUserId
      withAssigneeUserId.push(row)
    } else {
      withoutAssigneeUserId.push(row)
    }
  })

  return { withAssigneeUserId, withoutAssigneeUserId }
}
